import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CachePolicies } from "use-http";

import { useNotes } from "api/services/useNotes";
import {
  DETECTOR_GROUP_CREATE_NEW_OPTION,
  DETECTOR_GROUP_DEFAULT_OPTION,
} from "components/detectorManagement/FilterDetector/DetectorGroupAutocomplete/constants";
import { JANZU_URL } from "config/config";
import { DEFAULT_MAX_COUNT } from "config/constants";
import { useCreateDetectorForm } from "hooks/CreateDetector/useCreateDetectorForm";
import { useAppSelector } from "hooks/store";
import { useFetchCustom } from "hooks/useFetchCustom";
import { Typography } from "new-components/typography/Typography";
import { getUserState } from "store/slices/Auth/authSlice";
import { DetectorModes, DetectorModesReadable } from "types/createDetector";
import { CreatePredictorResponse, type CreateDetectorInput } from "types/detectors";
import { LocalNotes } from "types/notes";

import { useIsDetectorNameValid } from "./hooks";
import { Step1 } from "./steps/Step1";
import { Step2 } from "./steps/Step2";

export interface DetectorGroup {
  id: string;
  name: string;
  detectors_count: number;
  is_editable: boolean;
  is_deletable: boolean;
}

interface CreateDetectorProps {
  onPage?: boolean;
  onClose?: () => void;
  numDetectors?: number;
  dialogOpen: boolean;
  setDialogOpen: (value: boolean) => void;
}

export function CreateDetectorModal(props: CreateDetectorProps) {
  const { onPage, onClose, numDetectors, dialogOpen, setDialogOpen } = props;

  const userInfo = useAppSelector(getUserState);
  const theme = useTheme();
  const {
    currentStep,
    newDetectorForm,
    onInputChange,
    handleChangeStep,
    onSelectDetectorFormChange,
    onGroupChange,
    handleConfidenceThresholdChange,
    detectorCreationSettings,
    loading: loadingCreationSettings,
  } = useCreateDetectorForm({ numDetectors, userInfo });

  const {
    answerMode,
    maxCapCount,
    confidenceThreshold,
    detectorName,
    groupOption,
    groupName,
    labelPrompt,
    validBboxLabels,
  } = newDetectorForm;

  const navigate = useNavigate();

  const { error: detectorNameError, isValidating: isValidatingDetectorName } = useIsDetectorNameValid(
    detectorName,
    groupOption?.value
  );
  const [hasErrors, setHasErrors] = useState<boolean>(false);
  const [loadingCreateDetector, setLoadingCreateDetector] = useState(false);
  const [beforeCreationHook, setBeforeCreationHook] = useState<VoidFunction | null>(null);

  // Notes State
  const [notesData, setNotesData] = useState<LocalNotes>({
    CUSTOMER: [],
    GL: [],
  });

  const { bulkCreateNotesAsync } = useNotes();

  const handleClose = (reason: string) => {
    // If on the separate Create Detector page, call onClose() when a valid close reason is met
    // Possible reasons: cancel, backdropClick, escapeKeyDown (latter two are built into Modal)
    if (onPage && onClose) {
      if (reason === "cancel" || reason === "backdropClick") {
        onClose();
      }
    } else {
      setDialogOpen(false);
    }
  };

  const PredictorsUrl = `${JANZU_URL}device-api/predictors`;
  // Set up the CreatePredictor function
  const { post: createPredictor } = useFetchCustom<CreatePredictorResponse>(PredictorsUrl, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  const showCreateGroup = groupOption === DETECTOR_GROUP_CREATE_NEW_OPTION;

  function isValidForm() {
    // Valid if there is some entry for name and query and confidence threshold =/= 0
    const predictorIsValid = !!(
      detectorName?.length &&
      labelPrompt?.length &&
      (confidenceThreshold !== undefined || answerMode === DetectorModes.TEXT_RECOGNITION)
    );

    // Valid if some group is selected, or some name is entered for 'Create New Group' option
    let predictorSetIsValid = false;
    if (showCreateGroup) {
      predictorSetIsValid = !!groupName?.length;
    } else {
      predictorSetIsValid = groupOption !== undefined;
    }

    // Valid if there is at least one valid bbox label on count detector
    let countingObjectIsValid = true;
    if (answerMode === DetectorModes.COUNT) {
      countingObjectIsValid = !!validBboxLabels?.length;
    }

    let validMultichoiceOptions = true;
    if (answerMode === DetectorModes.MULTICHOICE) {
      validMultichoiceOptions = !!newDetectorForm.class_names?.length;
    }

    return predictorIsValid && predictorSetIsValid && countingObjectIsValid && validMultichoiceOptions;
  }

  function createFormDataFromObject(notesData: any): FormData {
    const formData = new FormData();
    let globalIndex = 0; // Global index for all notes

    Object.keys(notesData).forEach((category) => {
      const currentNotesData = notesData[category];
      currentNotesData.forEach((note: any, index: number) => {
        formData.append(`content_${globalIndex}`, note.content);
        formData.append(`is_pinned_${globalIndex}`, note.is_pinned);

        if (note.image) {
          formData.append(`image_${globalIndex}`, note.image, note.image.name);
        }

        formData.append(`note_type_${globalIndex}`, note.note_type);

        const createOrder = currentNotesData.length - index;
        formData.append(`order_${globalIndex}`, createOrder.toString());

        globalIndex += 1;
      });
    });
    return formData;
  }

  const onCreate = async () => {
    if (currentStep === 1) {
      handleChangeStep("next");
      return;
    }

    const isValid = isValidForm();
    if (isValid) {
      if (beforeCreationHook !== null) {
        beforeCreationHook();
        return;
      }

      let createDetectorInput: CreateDetectorInput = {
        name: detectorName,
        label_prompt: labelPrompt,
        confidence_threshold: confidenceThreshold,
        mode: answerMode,
        mode_configuration: {
          ...(answerMode === DetectorModes.COUNT ? { max_count: maxCapCount || DEFAULT_MAX_COUNT } : {}),
          ...(answerMode === DetectorModes.BOUNDING_BOX ? { max_num_bboxes: maxCapCount || DEFAULT_MAX_COUNT } : {}),
          ...(validBboxLabels ? { class_name: validBboxLabels } : {}),
        },
        ...(validBboxLabels && newDetectorForm.answerMode !== DetectorModes.COUNT
          ? { valid_bbox_labels: [validBboxLabels] }
          : {}),
        labeling_task: answerMode !== DetectorModes.COUNT ? "BINARY CLASSIFICATION" : "BOUNDING BOXES",
      };

      if (newDetectorForm.answerMode === DetectorModes.TEXT_RECOGNITION) {
        createDetectorInput = {
          mode: answerMode,
          label_prompt: labelPrompt,
          name: detectorName,
        };
      }

      // If groupId is -2, then then the group will not be sent, because the backend will assign the default value.
      if (showCreateGroup) {
        createDetectorInput = {
          ...createDetectorInput,
          predictor_set_name: groupName,
        };
      } else if (groupOption !== DETECTOR_GROUP_DEFAULT_OPTION) {
        createDetectorInput = {
          ...createDetectorInput,
          predictor_set_id: groupOption?.value,
        };
      }

      if (newDetectorForm.answerMode === DetectorModes.MULTICHOICE) {
        createDetectorInput = {
          ...createDetectorInput,
          mode_configuration: { class_names: JSON.parse(newDetectorForm?.class_names || "") },
        };
      }

      setLoadingCreateDetector(true);
      try {
        const response = await createPredictor(createDetectorInput);
        if (response?.id) {
          const formData = createFormDataFromObject(notesData);
          // Add detectorId to FormData for the mutation
          (formData as any).detectorId = response.id;
          const responseNotes = await bulkCreateNotesAsync(formData as any);
          if (responseNotes) {
            navigate(`/reef/detectors/${response.id}`);
          }
          return;
        }
        console.error("Failed to create the detector");
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingCreateDetector(false);
      }
    }
  };

  function capitalizeString(text: string): string {
    return text
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  const isDetectorNameValid = !isValidatingDetectorName && detectorNameError === "";

  const handleNextClick = () => {
    onCreate();
    setBeforeCreationHook(null);
  };

  const isFirstStepValid = useMemo(() => {
    if (!groupOption) {
      return false;
    }

    if (showCreateGroup && groupName.trim().length === 0) {
      return false;
    }

    return true;
  }, [showCreateGroup, groupName, groupOption]);

  return (
    <Dialog
      open={dialogOpen}
      scroll="paper"
      fullWidth
      maxWidth={useMediaQuery(theme.breakpoints.down("md")) ? "xl" : "md"}
      onClose={(_, reason) => {
        if (reason === "escapeKeyDown") {
          handleClose(reason);
        }
      }}
    >
      <DialogTitle className="bg-neutral-light">
        <Box className="flex justify-between items-center w-[95%] mx-auto">
          <Box className="w-[40px]" />
          <Typography variant="title" align="center">
            {currentStep === 1
              ? "Create Detector"
              : `Create Detector: ${detectorName} (${capitalizeString(DetectorModesReadable[answerMode])})`}
          </Typography>

          <Tooltip title="Close">
            <span>
              <IconButton edge="start" color="inherit" onClick={() => handleClose("cancel")} aria-label="close">
                <CloseIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent className="bg-zinc-100">
        <Box className="w-[95%] mx-auto">
          {currentStep === 1 ? (
            <Step1
              newDetectorForm={newDetectorForm}
              nameError={detectorNameError}
              onInputChange={onInputChange}
              onSelectDetectorFormChange={onSelectDetectorFormChange}
              onGroupChange={onGroupChange}
              showCreateGroup={showCreateGroup}
              detectorCreationSettings={detectorCreationSettings}
              isLoadingCreationSettings={loadingCreationSettings}
            />
          ) : (
            <Step2
              newDetectorForm={newDetectorForm}
              setBeforeCreationHook={setBeforeCreationHook}
              handleConfidenceThresholdChange={handleConfidenceThresholdChange}
              onInputChange={onInputChange}
              notesData={notesData}
              setNotesData={setNotesData}
              detectorCreationSettings={detectorCreationSettings}
              setHasErrors={setHasErrors}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions className="bg-neutral-light h-20 flex-col sm:flex-row gap-2 items-center justify-center">
        <Box className="p-3 mb-2 flex flex-col sm:flex-row justify-center w-[95%] gap-6">
          <Button
            onClick={() => (currentStep === 1 ? handleClose("cancel") : handleChangeStep("back"))}
            fullWidth
            variant="outlined"
            className="border-none text-neutral-dark max-w-[330px]"
          >
            {currentStep === 1 ? "Cancel" : "Back"}
          </Button>
          <Button
            onClick={handleNextClick}
            fullWidth
            variant="contained"
            disabled={
              !isDetectorNameValid ||
              loadingCreationSettings ||
              (currentStep === 2 && (!isValidForm() || hasErrors)) ||
              (currentStep === 1 && !isFirstStepValid)
            }
            className="bg-[#006DD9] max-w-[330px]"
            sx={{
              "&.Mui-disabled": {
                backgroundColor: "#eaeaea!important",
                color: "#c0c0c0",
              },
            }}
          >
            {currentStep === 1 ? "Next" : <Box>{loadingCreateDetector ? "Loading..." : "Create"}</Box>}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
